import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isManager } from "@/utils/roleHelpers";
import { useProductionSteps } from "@/hooks/useProductionSteps";
import { SECTOR_GROUPS } from "@/utils/constants";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronDown, AlertTriangle, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

interface SectorSwitcherProps {
  /** Variante compacta para uso em sidebars */
  compact?: boolean;
}

/**
 * Setores de produção (excluindo módulos dedicados como ARTE, COMERCIAL, LOGISTICA)
 * Derivado de SECTOR_GROUPS para evitar duplicação
 *
 * Exportado para que `tests/estoque-contencao-setor.test.ts` trave ESTA lista, e
 * não uma cópia dela: o teste redeclarava a derivação à mão e por isso ficaria
 * verde mesmo se alguém acrescentasse ESTOQUE aqui — que é exatamente a
 * regressão que ele existe para impedir (o switcher passaria a aparecer para
 * todo cortador que acumulasse o setor, realocando o login seguinte).
 */
export const PRODUCTION_ONLY_SECTORS: string[] = [
  ...SECTOR_GROUPS.corte.sectors,
  ...SECTOR_GROUPS.estamparia.sectors,
  ...SECTOR_GROUPS.suporte.sectors,
  ...SECTOR_GROUPS.expedicao.sectors,
];

/**
 * Componente para troca de setor ativo de PRODUÇÃO
 * - Só aparece para operadores com múltiplos setores de PRODUÇÃO
 * - Não lista módulos dedicados (ARTE, COMERCIAL, LOGISTICA)
 * - Managers não veem (têm acesso a tudo)
 */
export function SectorSwitcher({ compact = false }: SectorSwitcherProps) {
  const { userRole, setActiveSector } = useAuth();
  const [pendingSector, setPendingSector] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Buscar tarefas ativas do setor atual
  const { data: currentSteps = [] } = useProductionSteps(userRole?.setor_ativo || '');
  const activeTasks = currentSteps.filter(s => s.status === 'EM_EXECUCAO');
  
  // Filtrar apenas setores de produção (não módulos dedicados)
  const productionSectors = (userRole?.setores || []).filter((s: string) => 
    PRODUCTION_ONLY_SECTORS.includes(s)
  );
  
  // Não renderizar para managers, usuários sem role, ou com menos de 2 setores de produção
  if (!userRole || isManager(userRole) || productionSectors.length <= 1) {
    return null;
  }
  
  const handleSectorChange = async (newSector: string) => {
    if (newSector === userRole.setor_ativo) return;
    
    // Se tem tarefas ativas, mostrar confirmação
    if (activeTasks.length > 0) {
      setPendingSector(newSector);
      return;
    }
    
    await performSectorChange(newSector);
  };
  
  const performSectorChange = async (sector: string) => {
    setIsLoading(true);
    try {
      await setActiveSector(sector);
      toast.success(`Setor alterado para ${sector}`);
    } catch (error) {
      console.error('Error changing sector:', error);
      toast.error('Erro ao trocar de setor');
    } finally {
      setIsLoading(false);
      setPendingSector(null);
    }
  };
  
  // Setor atual (considerando apenas setores de produção)
  const currentSector = productionSectors.includes(userRole.setor_ativo || '') 
    ? userRole.setor_ativo 
    : productionSectors[0] || 'Setor';
  
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size={compact ? "sm" : "default"} 
            disabled={isLoading}
            className="gap-2"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Badge variant="secondary" className={compact ? "text-xs" : ""}>
                  {currentSector}
                </Badge>
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[150px]">
          {productionSectors.map((sector) => (
            <DropdownMenuItem
              key={sector}
              onClick={() => handleSectorChange(sector)}
              className="flex items-center justify-between"
            >
              <span>{sector}</span>
              {sector === userRole.setor_ativo && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Modal de confirmação para tarefas ativas */}
      <AlertDialog open={!!pendingSector} onOpenChange={() => setPendingSector(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Tarefas em Andamento
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Você tem <strong>{activeTasks.length}</strong> tarefa(s) em execução no setor{' '}
                <strong>{userRole.setor_ativo}</strong>.
              </p>
              <p className="text-muted-foreground">
                Ao trocar de setor, essas tarefas continuarão ativas. Você pode voltar a qualquer momento.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => pendingSector && performSectorChange(pendingSector)}
            >
              Trocar Mesmo Assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
